import { Variable, VariableSummary } from '@mtna/pojo-consumer-ui';
import { instanceOfCodeTabulationDatum, TabulationClassification, TabulationDatum } from '@mtna/rds-base-ui';

export type DatumValueType = 'STRING' | 'NUMBER' | 'CODE';
/**
 * Determines whether the specified Datum is unique inside the given Datum array.
 * @param datum Datum to check for uniqueness
 * @param allDatum Datum array to search
 * @returns whether the datum is unique in the given array of datum
 */
export function isDatumUnique(datum: TabulationDatum<any>, allDatum: TabulationDatum<any>[]): boolean {
  return !allDatum.some(dt => isMatchingDatum(dt, datum));
}

/**
 * Get the value from the datum. If the value is an object the optional `prop` can be specified.
 * @param d Datum to get the value
 * @param prop optional object property to return
 * @returns value of a datum
 */
export function getDatumValue(
  d: TabulationDatum<any>,
  variables: Array<Variable | VariableSummary>,
  classifications: { [id: string]: TabulationClassification },
  ...props: string[]
) {
  if (!d) {
    return null;
  } else if (typeof d.o === 'object') {
    return (!!props && props.length ? props : ['codeValue'])
      .map(prop => {
        if (prop === 'codeValue') {
          return d.o.cv;
        }
        if (prop === 'name') {
          const v = variables.find(v => v.uri === d.v);
          return !v ? d.o.cv : classifications[v.classificationUri].codes[d.o.cv];
        }
        return d.o[prop];
      })
      .join(': ');
  }
  return d.o;
}

/**
 * Determines if the 'value' & 'variable' between 2 datum are the same.
 * @param d1 Datum
 * @param d2 Datum
 * @returns whether the datum are equal
 */
export function isMatchingDatum(d1: TabulationDatum<any>, d2: TabulationDatum<any>): boolean {
  // If either are null or the variables aren't the same
  if (!d1 || !d2 || d1.v !== d2.v) {
    return false;
  }
  // If they have code values, compare code values
  if (instanceOfCodeTabulationDatum(d1) && instanceOfCodeTabulationDatum(d2)) {
    return d1.o.cv === d2.o.cv;
  }
  return d1.o === d2.o;
}
